import {
  CONVERTED_HEIC_FILE_EXTENSION,
  HEIC_FILE_EXTENSION_REGEX,
} from "../constants/chat";
import type {
  IAttachmentIdentity,
  IFilterDuplicateAttachmentsProps,
  IFilterDuplicateAttachmentsReturn,
} from "./attachment-identity.types";

// NOTE: Monotonic tail for runtimes with no Web Crypto at all — see
// `createAttachmentId`. Collision-free within one JS runtime, which is the only
// scope an attachment id ever lives in.
let attachmentIdSequence = 0;

// NOTE: Name after HEIC conversion (photo.heic -> photo.jpeg); others unchanged.
export const getConvertedHeicName = (name: string): string =>
  name.replace(HEIC_FILE_EXTENSION_REGEX, CONVERTED_HEIC_FILE_EXTENSION);

// NOTE: Opaque id for a locally added attachment. It must not be derived from
// the file name: two same-named attachments would then share an id, so removing
// one dropped both and upload progress/cancel hit the wrong entry (NSAI-4261 on
// web, NSAI-4882 on mobile — where the id was name + mtime, so any re-pick of
// the SAME file collided deterministically).
// Three tiers, because the three runtimes expose different crypto:
//   1. `crypto.randomUUID` — browsers in a secure context.
//   2. `crypto.getRandomValues` — no secure-context restriction, so on-premise
//      installs served over plain HTTP keep a CSPRNG rather than `Math.random`.
//      One drop can add several files inside the same millisecond, hence the
//      random suffix on top of the timestamp.
//   3. a counter — Hermes (React Native) ships no `crypto` global at all.
export const createAttachmentId = (): string => {
  // NOTE: `lib.dom` types `globalThis.crypto` as always present, but tier 3
  // exists precisely because Hermes (React Native) defines no such global —
  // the `in` check makes that absence expressible in the type without a cast.
  const webCrypto = "crypto" in globalThis ? globalThis.crypto : undefined;

  if (typeof webCrypto?.randomUUID === "function") {
    return webCrypto.randomUUID();
  }

  if (typeof webCrypto?.getRandomValues === "function") {
    const randomBytes = new Uint8Array(4);
    webCrypto.getRandomValues(randomBytes);
    const randomSuffix = Array.from(randomBytes, (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("");

    return `attachment-${Date.now()}-${randomSuffix}`;
  }

  attachmentIdSequence += 1;

  return `attachment-${Date.now()}-${attachmentIdSequence}`;
};

// NOTE: Splits incoming candidates into ones safe to attach vs. duplicates
// (already attached, or repeated inside this batch). Identity is the content
// hash when the caller could compute one, so a copy of an attached file is
// caught even after a rename, and two unrelated files stay distinct even when
// they share a name — browsers give every clipboard image the name `image.png`,
// which a name-only check read as a re-paste of the first screenshot
// (NSAI-4261).
// Entries with no hash fall back to converted name + byte size: that covers
// attachments which never had local bytes (saved assistant knowledge files,
// integration picks — size unknown too, so name alone decides), runtimes
// without `crypto.subtle`, and React Native, which picks files by URI and never
// hashes them. The fallback also keeps a .heic matching its converted .jpeg
// twin by name.
// Generic over the candidate so each platform can carry its own payload
// alongside the identity — a `File` on web, an `expo-document-picker` asset on
// mobile — instead of a second copy of this algorithm per platform.
export const filterDuplicateAttachments = <TCandidate>({
  candidates,
  toIdentity,
  existingIdentities,
}: IFilterDuplicateAttachmentsProps<TCandidate>): IFilterDuplicateAttachmentsReturn<TCandidate> => {
  const seenContentHashes = new Set<string>();
  const seenSizesByName = new Map<string, Set<number>>();
  const namesWithUnknownSize = new Set<string>();

  // NOTE: Name/size is recorded only for entries with no hash. Once both sides
  // are hashed the hash alone decides, so a shared name never merges two files.
  const rememberSeenAttachment = ({
    name,
    size,
    contentHash,
  }: IAttachmentIdentity): void => {
    if (contentHash) {
      seenContentHashes.add(contentHash);

      return;
    }

    const normalizedName = getConvertedHeicName(name);

    if (size === undefined) {
      namesWithUnknownSize.add(normalizedName);

      return;
    }

    const seenSizes = seenSizesByName.get(normalizedName) ?? new Set<number>();
    seenSizes.add(size);
    seenSizesByName.set(normalizedName, seenSizes);
  };

  const checkIfDuplicate = ({
    name,
    size,
    contentHash,
  }: IAttachmentIdentity): boolean => {
    const normalizedName = getConvertedHeicName(name);

    return (
      (contentHash !== undefined && seenContentHashes.has(contentHash)) ||
      namesWithUnknownSize.has(normalizedName) ||
      (size !== undefined &&
        (seenSizesByName.get(normalizedName)?.has(size) ?? false))
    );
  };

  for (const identity of existingIdentities) {
    rememberSeenAttachment(identity);
  }

  const uniqueCandidates: TCandidate[] = [];
  let hasDuplicates = false;

  for (const candidate of candidates) {
    const identity = toIdentity(candidate);

    if (checkIfDuplicate(identity)) {
      hasDuplicates = true;

      continue;
    }

    rememberSeenAttachment(identity);
    uniqueCandidates.push(candidate);
  }

  return { uniqueCandidates, hasDuplicates };
};
